import {
  BarChart3,
  Camera,
  Check,
  ChevronRight,
  CircleDollarSign,
  Copy,
  Download,
  FileUp,
  Gauge,
  Inbox,
  Mail,
  MapPin,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Table2,
  Target,
  Trash2,
  UploadCloud,
  Users,
  X,
} from "lucide-react";
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { campaigns, defaultSettings, sampleLeads, serviceStack } from "./data";
import type { CampaignFocus, Lead, LeadStatus, StudioSettings } from "./types";
import {
  companyShort,
  createSequence,
  downloadCsv,
  firstName,
  formatPercent,
  getCampaign,
  getLeadText,
  mapCsvRow,
  normalizeStatus,
  parseCsv,
  scoreLead,
  stableId,
  statusLabel,
} from "./utils";

type View = "overview" | "prospects" | "campaigns" | "stack" | "settings";

const storageKey = "photo-prospecting-dashboard-v3-gwinnett";

const navItems: Array<{ id: View; label: string; icon: typeof BarChart3 }> = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "prospects", label: "Prospects", icon: Users },
  { id: "campaigns", label: "Campaigns", icon: Target },
  { id: "stack", label: "Stack", icon: CircleDollarSign },
  { id: "settings", label: "Settings", icon: Settings },
];

const statuses: LeadStatus[] = ["new", "research", "ready", "sent", "replied", "booked", "do_not_contact"];

function loadState() {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as {
      leads: Lead[];
      selectedId: string;
      focus: CampaignFocus;
      settings: StudioSettings;
    };
  } catch {
    return null;
  }
}

function newManualLead(overrides: Partial<Lead> = {}): Lead {
  const lead: Lead = {
    id: "",
    company: "",
    website: "",
    industry: "",
    city: "",
    state: "GA",
    contactName: "",
    title: "",
    email: "",
    phone: "",
    linkedin: "",
    notes: "",
    signals: "",
    source: "Manual",
    status: "new",
    ...overrides,
  };
  lead.id = `${stableId(lead)}_${Date.now()}`;
  return lead;
}

function App() {
  const stored = typeof window !== "undefined" ? loadState() : null;
  const [view, setView] = useState<View>("overview");
  const [leads, setLeads] = useState<Lead[]>(stored?.leads ?? sampleLeads);
  const [selectedId, setSelectedId] = useState(stored?.selectedId ?? sampleLeads[0]?.id ?? "");
  const [focus, setFocus] = useState<CampaignFocus>(stored?.focus ?? "brand_refresh");
  const [settings, setSettings] = useState<StudioSettings>(stored?.settings ?? defaultSettings);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "all">("all");
  const [toast, setToast] = useState("");
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeCampaign = getCampaign(focus);

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify({ leads, selectedId, focus, settings }));
  }, [leads, selectedId, focus, settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 1800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const scoredLeads = useMemo(
    () =>
      leads
        .map((lead) => ({ ...lead, score: scoreLead(lead, activeCampaign) }))
        .sort((a, b) => b.score.score - a.score.score || a.company.localeCompare(b.company)),
    [leads, activeCampaign],
  );

  const filteredLeads = useMemo(() => {
    const text = query.trim().toLowerCase();
    return scoredLeads.filter((lead) => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (!text) return true;
      return getLeadText(lead).includes(text) || lead.email.toLowerCase().includes(text);
    });
  }, [scoredLeads, query, statusFilter]);

  const selectedLead = leads.find((lead) => lead.id === selectedId) ?? leads[0];
  const selectedScore = selectedLead ? scoreLead(selectedLead, activeCampaign) : null;
  const selectedSequence = selectedLead ? createSequence(selectedLead, activeCampaign, settings) : [];

  const stats = useMemo(() => {
    const total = leads.length;
    const ready = leads.filter((lead) => lead.status === "ready").length;
    const sent = leads.filter((lead) => lead.status === "sent").length;
    const replied = leads.filter((lead) => lead.status === "replied").length;
    const booked = leads.filter((lead) => lead.status === "booked").length;
    const dnc = leads.filter((lead) => lead.status === "do_not_contact").length;
    const avgScore = scoredLeads.length
      ? Math.round(scoredLeads.reduce((sum, lead) => sum + lead.score.score, 0) / scoredLeads.length)
      : 0;
    const sendCapacity = settings.dailyMailboxLimit * settings.mailboxCount;
    const projectedReplies = Math.round(sendCapacity * (settings.positiveReplyRate / 100));

    return { total, ready, sent, replied, booked, dnc, avgScore, sendCapacity, projectedReplies };
  }, [leads, scoredLeads, settings]);

  function notify(message: string) {
    setToast(message);
  }

  function updateLead(id: string, patch: Partial<Lead>) {
    setLeads((current) =>
      current.map((lead) => {
        if (lead.id !== id) return lead;
        const next = { ...lead, ...patch };
        const shouldRekey = patch.company || patch.website || patch.email || patch.city;
        if (shouldRekey) {
          next.id = stableId(next);
          setSelectedId(next.id);
        }
        return next;
      }),
    );
  }

  function importCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imported = parseCsv(String(reader.result || "")).map(mapCsvRow);
      setLeads((current) => {
        const byId = new Map(current.map((lead) => [lead.id, lead]));
        imported.forEach((lead) => {
          byId.set(lead.id, { ...byId.get(lead.id), ...lead });
        });
        return Array.from(byId.values());
      });
      if (imported[0]) setSelectedId(imported[0].id);
      notify(`Imported ${imported.length} leads`);
    };
    reader.readAsText(file);
    event.currentTarget.value = "";
  }

  function addManualLead(lead: Lead) {
    setLeads((current) => [lead, ...current]);
    setSelectedId(lead.id);
    setQuery("");
    setStatusFilter("all");
    setView("prospects");
    setIsAddLeadOpen(false);
    notify("Lead added");
  }

  function sendingRows() {
    return leads
      .filter((lead) => lead.status === "ready" && lead.email)
      .map((lead) => {
        const sequence = createSequence(lead, activeCampaign, settings);
        const score = scoreLead(lead, activeCampaign);
        return {
          email: lead.email,
          first_name: firstName(lead.contactName),
          full_name: lead.contactName,
          company: lead.company,
          title: lead.title,
          website: lead.website,
          city: lead.city,
          industry: lead.industry,
          fit_score: score.score,
          score_reasons: score.reasons.join("; "),
          subject_1: sequence[0]?.subject,
          email_1: sequence[0]?.body,
          subject_2: sequence[1]?.subject,
          email_2: sequence[1]?.body,
          subject_3: sequence[2]?.subject,
          email_3: sequence[2]?.body,
          source: lead.source,
        };
      });
  }

  function reviewRows() {
    return leads.map((lead) => {
      const score = scoreLead(lead, activeCampaign);
      return {
        company: lead.company,
        contact_name: lead.contactName,
        title: lead.title,
        email: lead.email,
        website: lead.website,
        industry: lead.industry,
        city: lead.city,
        state: lead.state,
        score: score.score,
        score_reasons: score.reasons.join("; "),
        signals: lead.signals,
        notes: lead.notes,
        status: lead.status,
        source: lead.source,
      };
    });
  }

  function exportSendingCsv() {
    const rows = sendingRows();
    if (!rows.length) {
      notify("Mark ready leads with emails before exporting");
      return;
    }
    downloadCsv("photo-prospecting-sending.csv", rows);
    notify(`Exported ${rows.length} ready leads`);
  }

  function exportReviewCsv() {
    downloadCsv("photo-prospecting-review.csv", reviewRows());
    notify("Review CSV exported");
  }

  async function copyText(text: string) {
    await navigator.clipboard.writeText(text);
    notify("Copied");
  }

  function resetWorkspace() {
    if (!window.confirm("Reset the dashboard to sample data?")) return;
    setLeads(sampleLeads);
    setSelectedId(sampleLeads[0]?.id ?? "");
    setFocus("brand_refresh");
    setSettings(defaultSettings);
    notify("Workspace reset");
  }

  return (
    <div className="app">
      <aside className="rail">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Camera size={21} />
          </div>
          <div>
            <strong>Gwinnett Photo</strong>
            <span>Prospecting</span>
          </div>
        </div>

        <nav className="nav-list" aria-label="Dashboard">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? "nav-item active" : "nav-item"}
                onClick={() => setView(item.id)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="rail-footer">
          <div className="mini-metric">
            <span>Daily capacity</span>
            <strong>{stats.sendCapacity}</strong>
          </div>
          <div className="mini-metric">
            <span>Projected replies</span>
            <strong>{stats.projectedReplies}</strong>
          </div>
        </div>
      </aside>

      <div className="shell">
        <header className="topbar">
          <div>
            <p className="eyebrow">Gwinnett County small business photography</p>
            <h1>Local Prospecting Dashboard</h1>
          </div>
          <div className="top-actions">
            <input ref={fileInputRef} className="hidden-file" type="file" accept=".csv,text/csv" onChange={importCsv} />
            <button className="button primary" onClick={() => setIsAddLeadOpen(true)}>
              <Plus size={17} />
              Add Lead
            </button>
            <button className="button" onClick={() => fileInputRef.current?.click()}>
              <FileUp size={17} />
              Import CSV
            </button>
            <button className="button" onClick={exportSendingCsv}>
              <Download size={17} />
              Export Ready
            </button>
            <button className="icon-button" aria-label="Export review CSV" title="Export review CSV" onClick={exportReviewCsv}>
              <Table2 size={18} />
            </button>
          </div>
        </header>

        <main className="main">
          {view === "overview" && (
            <OverviewView
              stats={stats}
              leads={scoredLeads}
              activeCampaign={activeCampaign.label}
              setView={setView}
              setSelectedId={setSelectedId}
            />
          )}

          {view === "prospects" && (
            <ProspectsView
              query={query}
              statusFilter={statusFilter}
              filteredLeads={filteredLeads}
              selectedLead={selectedLead}
              selectedScore={selectedScore}
              selectedSequence={selectedSequence}
              onQuery={setQuery}
              onStatusFilter={setStatusFilter}
              onSelect={setSelectedId}
              onUpdateLead={updateLead}
              onCopy={copyText}
            />
          )}

          {view === "campaigns" && (
            <CampaignsView
              focus={focus}
              onFocus={setFocus}
              settings={settings}
              onSettings={setSettings}
              selectedLead={selectedLead}
              selectedSequence={selectedSequence}
            />
          )}

          {view === "stack" && <StackView stats={stats} />}

          {view === "settings" && (
            <SettingsView
              settings={settings}
              onSettings={setSettings}
              stats={stats}
              onReset={resetWorkspace}
            />
          )}
        </main>
      </div>

      {isAddLeadOpen && (
        <AddLeadModal
          onClose={() => setIsAddLeadOpen(false)}
          onAdd={addManualLead}
        />
      )}

      <div className={toast ? "toast show" : "toast"}>{toast}</div>
    </div>
  );
}

function AddLeadModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (lead: Lead) => void;
}) {
  const [draft, setDraft] = useState<Lead>(() =>
    newManualLead({
      city: "Lawrenceville",
      industry: "Residential realtor",
      signals: "Gwinnett, local business, needs updated photos",
    }),
  );
  const canAdd = draft.company.trim().length > 1;

  function updateDraft(patch: Partial<Lead>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canAdd) return;
    onAdd(
      newManualLead({
        ...draft,
        company: draft.company.trim(),
        contactName: draft.contactName.trim(),
        email: draft.email.trim(),
        website: draft.website.trim(),
        source: draft.source || "Manual",
      }),
    );
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <form className="modal-panel" role="dialog" aria-modal="true" aria-labelledby="add-lead-title" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2 id="add-lead-title">Add Prospect</h2>
            <p>Manual entry for Gwinnett realtors, freelancers, and small businesses.</p>
          </div>
          <button className="icon-button" type="button" aria-label="Close add lead" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body form-grid">
          <TextField label="Company or brand" value={draft.company} onChange={(company) => updateDraft({ company })} />
          <TextField label="Contact" value={draft.contactName} onChange={(contactName) => updateDraft({ contactName })} />
          <TextField label="Title" value={draft.title} onChange={(title) => updateDraft({ title })} />
          <TextField label="Email" value={draft.email} onChange={(email) => updateDraft({ email })} />
          <TextField label="Website" value={draft.website} onChange={(website) => updateDraft({ website })} />
          <TextField label="Industry" value={draft.industry} onChange={(industry) => updateDraft({ industry })} />
          <TextField label="City" value={draft.city} onChange={(city) => updateDraft({ city })} />
          <TextField label="State" value={draft.state} onChange={(state) => updateDraft({ state })} />
          <label className="field">
            <span>Status</span>
            <select value={draft.status} onChange={(event) => updateDraft({ status: normalizeStatus(event.target.value) })}>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </label>
          <TextArea label="Signals" value={draft.signals} onChange={(signals) => updateDraft({ signals })} />
          <TextArea label="Notes" value={draft.notes} onChange={(notes) => updateDraft({ notes })} />
        </div>

        <div className="modal-actions">
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" type="submit" disabled={!canAdd}>
            <Plus size={17} />
            Add Prospect
          </button>
        </div>
      </form>
    </div>
  );
}

function OverviewView({
  stats,
  leads,
  activeCampaign,
  setView,
  setSelectedId,
}: {
  stats: ReturnType<typeof buildStatsShape>;
  leads: Array<Lead & { score: ReturnType<typeof scoreLead> }>;
  activeCampaign: string;
  setView: (view: View) => void;
  setSelectedId: (id: string) => void;
}) {
  const nextBest = leads.filter((lead) => !["sent", "booked", "do_not_contact"].includes(lead.status)).slice(0, 5);
  const totalPipeline = Math.max(1, stats.total);

  return (
    <section className="view-grid">
      <div className="metrics-grid">
        <MetricCard icon={Users} label="Total leads" value={stats.total} detail={`${stats.ready} ready`} />
        <MetricCard icon={Gauge} label="Avg fit score" value={stats.avgScore} detail={activeCampaign} />
        <MetricCard icon={Mail} label="Sent" value={stats.sent} detail={`${stats.replied} replies`} />
        <MetricCard icon={Camera} label="Booked" value={stats.booked} detail={`${stats.dnc} suppressed`} />
      </div>

      <section className="panel wide-panel">
        <div className="panel-head">
          <div>
            <h2>Pipeline</h2>
            <p>Current lead movement by status.</p>
          </div>
          <button className="button ghost" onClick={() => setView("prospects")}>
            Open prospects
            <ChevronRight size={17} />
          </button>
        </div>
        <div className="pipeline-grid">
          {statuses.map((status) => {
            const count = leads.filter((lead) => lead.status === status).length;
            return (
              <div className="pipeline-cell" key={status}>
                <div className="pipeline-label">
                  <span>{statusLabel(status)}</span>
                  <strong>{count}</strong>
                </div>
                <div className="bar-track">
                  <span style={{ width: `${(count / totalPipeline) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Next Best Leads</h2>
            <p>Highest fit accounts not yet closed out.</p>
          </div>
        </div>
        <div className="lead-stack">
          {nextBest.map((lead) => (
            <button
              className="lead-strip"
              key={lead.id}
              onClick={() => {
                setSelectedId(lead.id);
                setView("prospects");
              }}
            >
              <ScoreBadge score={lead.score.score} />
              <span>
                <strong>{lead.company}</strong>
                <small>{[lead.industry, lead.city].filter(Boolean).join(" / ") || "Needs research"}</small>
              </span>
              <StatusPill status={lead.status} />
            </button>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Volume</h2>
            <p>Capacity based on configured mailboxes.</p>
          </div>
        </div>
        <div className="volume-grid">
          <div>
            <span>Daily sends</span>
            <strong>{stats.sendCapacity}</strong>
          </div>
          <div>
            <span>Projected replies</span>
            <strong>{stats.projectedReplies}</strong>
          </div>
          <div>
            <span>Ready runway</span>
            <strong>{stats.sendCapacity ? Math.ceil(stats.ready / stats.sendCapacity) : 0} days</strong>
          </div>
        </div>
      </section>
    </section>
  );
}

function ProspectsView({
  query,
  statusFilter,
  filteredLeads,
  selectedLead,
  selectedScore,
  selectedSequence,
  onQuery,
  onStatusFilter,
  onSelect,
  onUpdateLead,
  onCopy,
}: {
  query: string;
  statusFilter: LeadStatus | "all";
  filteredLeads: Array<Lead & { score: ReturnType<typeof scoreLead> }>;
  selectedLead?: Lead;
  selectedScore: ReturnType<typeof scoreLead> | null;
  selectedSequence: ReturnType<typeof createSequence>;
  onQuery: (value: string) => void;
  onStatusFilter: (value: LeadStatus | "all") => void;
  onSelect: (id: string) => void;
  onUpdateLead: (id: string, patch: Partial<Lead>) => void;
  onCopy: (value: string) => void;
}) {
  return (
    <section className="prospects-layout">
      <div className="panel prospects-panel">
        <div className="panel-head split">
          <div>
            <h2>Prospects</h2>
            <p>{filteredLeads.length} accounts in view.</p>
          </div>
          <div className="filter-row">
            <label className="search-box">
              <Search size={17} />
              <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search leads" />
            </label>
            <select value={statusFilter} onChange={(event) => onStatusFilter(event.target.value as LeadStatus | "all")}>
              <option value="all">All statuses</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Score</th>
                <th>Company</th>
                <th>Contact</th>
                <th>Market</th>
                <th>Status</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className={selectedLead?.id === lead.id ? "selected" : ""}
                  onClick={() => onSelect(lead.id)}
                >
                  <td>
                    <ScoreBadge score={lead.score.score} />
                  </td>
                  <td>
                    <strong>{lead.company}</strong>
                    <small>{lead.industry || "Needs industry"}</small>
                  </td>
                  <td>
                    {lead.contactName || "Research"}
                    <small>{lead.title || "No title"}</small>
                  </td>
                  <td>{[lead.city, lead.state].filter(Boolean).join(", ") || "Open"}</td>
                  <td>
                    <StatusPill status={lead.status} />
                  </td>
                  <td>{lead.email ? "Ready" : "Missing"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel detail-panel">
        {selectedLead && selectedScore ? (
          <LeadEditor
            lead={selectedLead}
            score={selectedScore}
            sequence={selectedSequence}
            onUpdate={(patch) => onUpdateLead(selectedLead.id, patch)}
            onCopy={onCopy}
          />
        ) : (
          <div className="empty-state">No prospect selected.</div>
        )}
      </div>
    </section>
  );
}

function LeadEditor({
  lead,
  score,
  sequence,
  onUpdate,
  onCopy,
}: {
  lead: Lead;
  score: ReturnType<typeof scoreLead>;
  sequence: ReturnType<typeof createSequence>;
  onUpdate: (patch: Partial<Lead>) => void;
  onCopy: (value: string) => void;
}) {
  return (
    <div className="detail-inner">
      <div className="detail-title-row">
        <div>
          <h2>{lead.company}</h2>
          <p>{[lead.industry, lead.city, lead.state].filter(Boolean).join(" / ") || "Lead detail"}</p>
        </div>
        <ScoreBadge score={score.score} large />
      </div>

      <div className="reason-row">
        {score.reasons.map((reason) => (
          <span className="reason-chip" key={reason}>
            {reason}
          </span>
        ))}
      </div>

      <div className="form-grid">
        <TextField label="Company" value={lead.company} onChange={(value) => onUpdate({ company: value })} />
        <TextField label="Contact" value={lead.contactName} onChange={(value) => onUpdate({ contactName: value })} />
        <TextField label="Title" value={lead.title} onChange={(value) => onUpdate({ title: value })} />
        <TextField label="Email" value={lead.email} onChange={(value) => onUpdate({ email: value })} />
        <TextField label="Website" value={lead.website} onChange={(value) => onUpdate({ website: value })} />
        <TextField label="Industry" value={lead.industry} onChange={(value) => onUpdate({ industry: value })} />
        <TextField label="City" value={lead.city} onChange={(value) => onUpdate({ city: value })} />
        <TextField label="State" value={lead.state} onChange={(value) => onUpdate({ state: value })} />
        <label className="field">
          <span>Status</span>
          <select value={lead.status} onChange={(event) => onUpdate({ status: normalizeStatus(event.target.value) })}>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </select>
        </label>
        <TextArea label="Signals" value={lead.signals} onChange={(value) => onUpdate({ signals: value })} />
        <TextArea label="Notes" value={lead.notes} onChange={(value) => onUpdate({ notes: value })} />
      </div>

      <div className="sequence-stack">
        {sequence.map((email) => (
          <article className="email-block" key={email.step}>
            <div className="email-head">
              <div>
                <strong>{email.step}</strong>
                <span>{email.subject}</span>
              </div>
              <button className="icon-button" aria-label={`Copy ${email.step}`} onClick={() => onCopy(`Subject: ${email.subject}\n\n${email.body}`)}>
                <Copy size={17} />
              </button>
            </div>
            <pre>{email.body}</pre>
          </article>
        ))}
      </div>
    </div>
  );
}

function CampaignsView({
  focus,
  onFocus,
  settings,
  onSettings,
  selectedLead,
  selectedSequence,
}: {
  focus: CampaignFocus;
  onFocus: (focus: CampaignFocus) => void;
  settings: StudioSettings;
  onSettings: (settings: StudioSettings) => void;
  selectedLead?: Lead;
  selectedSequence: ReturnType<typeof createSequence>;
}) {
  return (
    <section className="view-grid">
      <div className="campaign-grid">
        {campaigns.map((campaign) => (
          <button
            key={campaign.id}
            className={focus === campaign.id ? "campaign-tile active" : "campaign-tile"}
            onClick={() => onFocus(campaign.id)}
          >
            <div className="tile-icon">
              <Sparkles size={18} />
            </div>
            <strong>{campaign.label}</strong>
            <span>{campaign.angle}</span>
          </button>
        ))}
      </div>

      <section className="panel wide-panel">
        <div className="panel-head">
          <div>
            <h2>{getCampaign(focus).label}</h2>
            <p>{getCampaign(focus).angle}</p>
          </div>
        </div>
        <div className="campaign-detail-grid">
          <ListBlock title="Ideal Accounts" items={getCampaign(focus).idealAccounts} />
          <ListBlock title="Triggers" items={getCampaign(focus).triggers} />
          <ListBlock title="Value Props" items={getCampaign(focus).valueProps} />
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Offer</h2>
            <p>Used in generated email copy.</p>
          </div>
        </div>
        <div className="panel-body">
          <TextArea
            label="Core offer"
            value={settings.offer}
            onChange={(offer) => onSettings({ ...settings, offer })}
          />
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Preview</h2>
            <p>{selectedLead ? companyShort(selectedLead) : "No selected lead"}</p>
          </div>
        </div>
        <div className="panel-body preview-copy">
          <strong>{selectedSequence[0]?.subject}</strong>
          <pre>{selectedSequence[0]?.body}</pre>
        </div>
      </section>
    </section>
  );
}

function StackView({ stats }: { stats: ReturnType<typeof buildStatsShape> }) {
  return (
    <section className="view-grid">
      <section className="panel wide-panel">
        <div className="panel-head">
          <div>
            <h2>Operating Stack</h2>
            <p>Paid services where they speed up volume.</p>
          </div>
        </div>
        <div className="stack-grid">
          {serviceStack.map((item) => (
            <article className="stack-item" key={item.stage}>
              <div className="stack-stage">
                <ShieldCheck size={18} />
                <strong>{item.stage}</strong>
              </div>
              <dl>
                <div>
                  <dt>Primary</dt>
                  <dd>{item.primary}</dd>
                </div>
                <div>
                  <dt>Fallback</dt>
                  <dd>{item.fallback}</dd>
                </div>
              </dl>
              <p>{item.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Readiness</h2>
            <p>Current send preparation.</p>
          </div>
        </div>
        <div className="readiness-list">
          <ReadinessItem ok={stats.ready > 0} label={`${stats.ready} ready leads`} />
          <ReadinessItem ok={stats.dnc > 0} label={`${stats.dnc} suppressed contacts`} />
          <ReadinessItem ok={stats.avgScore >= 60} label={`${stats.avgScore} average score`} />
          <ReadinessItem ok={stats.sendCapacity <= 80} label={`${stats.sendCapacity} daily sends`} />
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Cloudflare Pages</h2>
            <p>Build command and output directory.</p>
          </div>
        </div>
        <div className="command-list">
          <code>npm run build</code>
          <code>dist</code>
          <code>npx wrangler pages deploy dist</code>
        </div>
      </section>
    </section>
  );
}

function SettingsView({
  settings,
  onSettings,
  stats,
  onReset,
}: {
  settings: StudioSettings;
  onSettings: (settings: StudioSettings) => void;
  stats: ReturnType<typeof buildStatsShape>;
  onReset: () => void;
}) {
  return (
    <section className="settings-layout">
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Studio</h2>
            <p>Sender identity and market.</p>
          </div>
        </div>
        <div className="panel-body form-grid">
          <TextField label="Sender" value={settings.senderName} onChange={(senderName) => onSettings({ ...settings, senderName })} />
          <TextField label="Studio" value={settings.studioName} onChange={(studioName) => onSettings({ ...settings, studioName })} />
          <TextField label="Market" value={settings.market} onChange={(market) => onSettings({ ...settings, market })} />
          <TextField
            label="Postal address"
            value={settings.postalAddress}
            onChange={(postalAddress) => onSettings({ ...settings, postalAddress })}
          />
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Volume Model</h2>
            <p>Capacity and reply planning.</p>
          </div>
        </div>
        <div className="panel-body form-grid">
          <NumberField
            label="Mailboxes"
            value={settings.mailboxCount}
            onChange={(mailboxCount) => onSettings({ ...settings, mailboxCount })}
          />
          <NumberField
            label="Daily sends per mailbox"
            value={settings.dailyMailboxLimit}
            onChange={(dailyMailboxLimit) => onSettings({ ...settings, dailyMailboxLimit })}
          />
          <NumberField
            label="Positive reply rate"
            value={settings.positiveReplyRate}
            suffix="%"
            onChange={(positiveReplyRate) => onSettings({ ...settings, positiveReplyRate })}
          />
          <div className="calc-box">
            <span>Daily capacity</span>
            <strong>{stats.sendCapacity}</strong>
            <small>{formatPercent(settings.positiveReplyRate)} positive reply model</small>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Workspace</h2>
            <p>Local browser storage controls.</p>
          </div>
        </div>
        <div className="danger-zone">
          <button className="button danger" onClick={onReset}>
            <Trash2 size={17} />
            Reset sample data
          </button>
        </div>
      </section>
    </section>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <article className="metric-card">
      <div className="metric-icon">
        <Icon size={18} />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function ScoreBadge({ score, large = false }: { score: number; large?: boolean }) {
  const tone = score >= 75 ? "high" : score >= 45 ? "medium" : "low";
  return <span className={`score-badge ${tone} ${large ? "large" : ""}`}>{score}</span>;
}

function StatusPill({ status }: { status: LeadStatus }) {
  return <span className={`status-pill ${status}`}>{statusLabel(status)}</span>;
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field wide-field">
      <span>{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberField({
  label,
  value,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="number-wrap">
        <input
          type="number"
          min="0"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        {suffix && <b>{suffix}</b>}
      </div>
    </label>
  );
}

function ListBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="list-block">
      <strong>{title}</strong>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function ReadinessItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={ok ? "readiness-item ok" : "readiness-item warn"}>
      {ok ? <Check size={16} /> : <UploadCloud size={16} />}
      <span>{label}</span>
    </div>
  );
}

function buildStatsShape() {
  return {
    total: 0,
    ready: 0,
    sent: 0,
    replied: 0,
    booked: 0,
    dnc: 0,
    avgScore: 0,
    sendCapacity: 0,
    projectedReplies: 0,
  };
}

export default App;
